import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  CalendarDays,
  Car,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Download,
  FerrisWheel,
  Landmark,
  MapPin,
  MessageSquareText,
  PhoneCall,
  Telescope,
} from "lucide-react";
import {
  formatPackagePrice,
} from "../Packages/packages";
import {
  getBookingPlaceSlug,
} from "./Booking.js";
import "./Booking.css";


export default function Booking() {
    const navigate = useNavigate();
  const location = useLocation();
  const { place = "", packageSlug = "" } = useParams();

  const placeSlug = getBookingPlaceSlug(place);

  const API_BASE =
  (
    typeof process !== "undefined" && process.env?.REACT_APP_API_URL
      ? process.env.REACT_APP_API_URL
      : "http://localhost:5000"
  ).replace(/\/$/, "");

  const statePackage = location.state?.package || null;

  const [pkg, setPkg] = useState(null);
  const [packageLoading, setPackageLoading] = useState(true);
  const [packageError, setPackageError] = useState("");

  const destination =
  pkg?.place ||
  pkg?.location ||
  placeSlug
    .split("-")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  const fromCity = location.state?.from || "New Delhi";

  useEffect(() => {
    let cancelled = false;

    const loadAuthoritativePackage = async () => {
      setPackageLoading(true);
      setPackageError("");
      setPkg(null);

      try {
        let packageData = null;

        /*
         * First choice:
         * If Packages.jsx passed the real database package ID,
         * fetch that exact package from the backend.
         */
        if (statePackage?.id) {
          const response = await fetch(
            `${API_BASE}/api/packages/${encodeURIComponent(statePackage.id)}`
          );

          if (response.status === 404) {
            throw new Error("Package is no longer available.");
          }

          if (!response.ok) {
            throw new Error("Unable to load package.");
          }

          const data = await response.json();
          packageData = data?.data || data;
        } else {
          /*
           * Direct URL entry:
           * use the existing public package endpoint and find the
           * exact package matching the URL.
           */
          if (!placeSlug || !packageSlug) {
            throw new Error("Invalid package URL.");
          }

          const response = await fetch(
            `${API_BASE}/api/packages?place=${encodeURIComponent(placeSlug)}`
          );

          if (!response.ok) {
            throw new Error("Unable to load packages.");
          }

          const data = await response.json();
          const packages = Array.isArray(data)
            ? data
            : Array.isArray(data?.data)
              ? data.data
              : [];

          const normalizeSlug = (value = "") =>
            String(value)
              .trim()
              .toLowerCase()
              .replace(/&/g, "and")
              .replace(/[^a-z0-9]+/g, "-")
              .replace(/^-+|-+$/g, "");

          packageData =
            packages.find(
              (item) => normalizeSlug(item.title) === normalizeSlug(packageSlug)
            ) || null;

          if (!packageData) {
            throw new Error("Package not found.");
          }

          /*
           * Re-fetch the exact package by ID so the booking page
           * always uses the authoritative database record.
           */
          if (packageData.id) {
            const exactResponse = await fetch(
              `${API_BASE}/api/packages/${encodeURIComponent(packageData.id)}`
            );

            if (exactResponse.status === 404) {
              throw new Error("Package is no longer available.");
            }

            if (!exactResponse.ok) {
              throw new Error("Unable to load package.");
            }

            const exactData = await exactResponse.json();
            packageData = exactData?.data || exactData;
          }
        }

        if (!packageData?.id) {
          throw new Error("Invalid package returned by server.");
        }

        if (!cancelled) {
          setPkg({
            ...packageData,
            image: packageData.image_url || packageData.image || "",
            place:
              packageData.place ||
              packageData.location ||
              placeSlug,
          });
        }
      } catch (error) {
        if (!cancelled) {
          setPackageError(error.message || "Unable to load package.");
          setPkg(null);
        }
      } finally {
        if (!cancelled) {
          setPackageLoading(false);
        }
      }
    };

    loadAuthoritativePackage();

    return () => {
      cancelled = true;
    };
  }, [API_BASE, packageSlug, placeSlug, statePackage?.id]);

  const parsePackageArray = (value) => {
  if (Array.isArray(value)) return value;

  if (!value) return [];

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }
  }

  return [];
};

const places = useMemo(() => {
  if (!pkg?.location) return [];

  return String(pkg.location)
    .split("|")
    .map((item) => item.trim())
    .filter(Boolean);
}, [pkg?.location]);

const itinerary = useMemo(
  () => parsePackageArray(pkg?.itinerary),
  [pkg?.itinerary]
);

const inclusions = useMemo(
  () => parsePackageArray(pkg?.inclusions),
  [pkg?.inclusions]
);

const exclusions = useMemo(
  () => parsePackageArray(pkg?.exclusions),
  [pkg?.exclusions]
);

const highlights = useMemo(
  () => parsePackageArray(pkg?.highlights),
  [pkg?.highlights]
);

const facilities = useMemo(
  () => parsePackageArray(pkg?.facilities),
  [pkg?.facilities]
);

const departureDates = useMemo(
  () => parsePackageArray(pkg?.departure_dates),
  [pkg?.departure_dates]
);

const [activeTab, setActiveTab] = useState("overview");

  const [showMoreHotels, setShowMoreHotels] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedTravelDate, setSelectedTravelDate] = useState(location.state?.travelDate || "");
  const [travelDateError, setTravelDateError] = useState("");

  const formatDate = (value) => {
  if (!value) return "—";

  const datePart = String(value).slice(0, 10);
  const [year, month, day] = datePart.split("-");

  if (!year || !month || !day) return "—";

  return new Date(
    Number(year),
    Number(month) - 1,
    Number(day)
  ).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};
  
  const getToday = () => new Date().toISOString().split("T")[0];

const isValidDepartureDate = (dateValue) => {
  if (!dateValue || !pkg) return false;

  const date = new Date(`${dateValue}T00:00:00`);
  const today = new Date(`${getToday()}T00:00:00`);

  // Booking window
  if (pkg.booking_start_date && dateValue < pkg.booking_start_date) {
    return false;
  }

  if (pkg.booking_end_date && dateValue > pkg.booking_end_date) {
    return false;
  }

  // Minimum advance booking
  const minAdvance = Number(pkg.booking_min_advance_days || 0);
  const minimumDate = new Date(today);
  minimumDate.setDate(minimumDate.getDate() + minAdvance);

  if (date < minimumDate) {
    return false;
  }

  // Maximum advance booking
  const maxYears = Number(pkg.booking_max_advance_years || 0);

  if (maxYears > 0) {
    const maximumDate = new Date(today);
    maximumDate.setFullYear(maximumDate.getFullYear() + maxYears);

    if (date > maximumDate) {
      return false;
    }
  }

  // Daily
  if (pkg.departure_type === "daily") {
    return true;
  }

  // Recurring
  if (pkg.departure_type === "recurring") {
    if (!pkg.departure_start_date) return false;

    const startDate = new Date(`${pkg.departure_start_date}T00:00:00`);
    const interval = Number(pkg.departure_interval_days || 0);

    if (interval < 1 || date < startDate) return false;

    const diffDays = Math.round(
      (date - startDate) / (1000 * 60 * 60 * 24)
    );

    return diffDays % interval === 0;
  }

  // Fixed dates
  if (pkg.departure_type === "fixed") {
  return departureDates.includes(dateValue);
}

  return false;
};

  const scrollToSection = (id) => {
    setActiveTab(id);
    requestAnimationFrame(() => {
      document.getElementById(`holiday-${id}`)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  const handleDownloadPdf = async () => {
  if (!pkg?.id) {
    alert("Package information is unavailable.");
    return;
  }

  try {
    const response = await fetch(
      `${API_BASE}/api/packages/${encodeURIComponent(pkg.id)}/pdf`
    );

    if (!response.ok) {
      throw new Error(`PDF generation failed: ${response.status}`);
    }

    const blob = await response.blob();

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `${String(pkg.title || "holiday-package")
      .replace(/[^a-z0-9]+/gi, "-")
      .replace(/^-+|-+$/g, "")
      .toLowerCase()}.pdf`;

    document.body.appendChild(link);
    link.click();
    link.remove();

    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Holiday package PDF download failed:", error);
    alert("Unable to download the PDF. Please try again.");
  }
};

    if (packageLoading) {
    return (
      <main className="holiday-booking-page">
        <section className="holiday-booking-empty">
          <h1>Loading package...</h1>
          <p>Please wait while we load the latest package details.</p>
        </section>
      </main>
    );
  }

  if (packageError || !pkg) {
    return (
      <main className="holiday-booking-page">
        <section className="holiday-booking-empty">
          <h1>Package not available</h1>
          <p>{packageError || "This package could not be found."}</p>

          <button
            type="button"
            onClick={() =>
              navigate(
                placeSlug
                  ? `/holidays/packages/${placeSlug}`
                  : "/holidays"
              )
            }
          >
            Back to Packages
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="holiday-booking-page">
      <section className="holiday-booking-shell">
        <div className="holiday-booking-breadcrumb">
          <button type="button" onClick={() => navigate("/holidays")}>Home</button>
          <span>&gt;</span>
          <button type="button" onClick={() => navigate(`/holidays/packages/${placeSlug}`)}>Holidays</button>
          <span>&gt;</span>
          <strong>{pkg.title}</strong>
        </div>

        <div className="holiday-booking-head">
          <div>
            <h1>{pkg.title}</h1>

<div className="holiday-booking-meta">
  {pkg.duration && (
    <strong>{pkg.duration.replace("/", " / ")}</strong>
  )}

  {pkg.group_size && (
    <span>Up to {pkg.group_size} guests per booking</span>
  )}

  {(pkg.category_name || pkg.category_label) && (
    <span>{pkg.category_name || pkg.category_label}</span>
  )}
</div>

            <p>{places.join(" | ") || `${fromCity} | ${destination}`}</p>
            {(pkg.rating !== null &&
  pkg.rating !== undefined &&
  pkg.rating !== "") && (
  <div className="holiday-package-rating">
    <strong>★ {Number(pkg.rating).toFixed(1)}</strong>
  </div>
)}
          </div>
          <button
  className="holiday-booking-pdf"
  type="button"
  onClick={handleDownloadPdf}
>
  <Download size={16} />
  Download PDF
</button>
        </div>

        <div className="holiday-booking-grid">
          <section className="holiday-booking-main">
            <div className="holiday-booking-gallery">
              {pkg.image ? (
  <img src={pkg.image} alt={pkg.title} />
) : (
  <div className="holiday-booking-image-placeholder">
    Package image unavailable
  </div>
)}
              <button className="holiday-gallery-arrow holiday-gallery-arrow--left" type="button" aria-label="Previous image">
                <ChevronLeft size={22} />
              </button>
              <button className="holiday-gallery-arrow holiday-gallery-arrow--right" type="button" aria-label="Next image">
                <ChevronRight size={22} />
              </button>
            </div>

            <div className="holiday-detail-tabs" aria-label="Package details">
              {[
  ["overview", "Overview"],
  ["itinerary", "Day wise Itinerary"],
  ["inclusion", "Inclusion/Exclusions"],
].map(([id, label]) => (
                <button
                  type="button"
                  key={id}
                  className={activeTab === id ? "is-active" : ""}
                  onClick={() => scrollToSection(id)}
                >
                  {label}
                </button>
              ))}
            </div>

            <section id="holiday-overview" className="holiday-scroll-section">
  <div className="holiday-tab-panel">
    <h2>Package Overview</h2>

    {pkg.description && (
      <p>{pkg.description}</p>
    )}

    {highlights.length > 0 && (
      <div className="holiday-highlights-section">
        <h3>Highlights</h3>

        <ul>
          {highlights.map((item, index) => (
            <li key={`highlight-${index}`}>
              {typeof item === "object"
                ? item.name || item.title || item.description || ""
                : item}
            </li>
          ))}
        </ul>
      </div>
    )}

    {!pkg.description && highlights.length === 0 && (
      <p>
        Package details are available as provided by the administrator.
      </p>
    )}
  </div>
</section>

            <section id="holiday-itinerary" className="holiday-scroll-section">
              <div className="holiday-tab-panel holiday-day-panel">
                <h2>Day wise Itinerary</h2>
                <div className="holiday-day-timeline">
                  {itinerary.map((item) => (
                    <div className="holiday-day-row" key={`${item.day}-tab-${item.title}`}>
                      <span className="holiday-day-badge">
  {String(item.day ?? "").replace(" ", "\n")}
</span>
                      <span className="holiday-day-dot" />
                      <article>
  <h3>{item.title || `Day ${item.day}`}</h3>

  {item.description && (
    <p>{item.description}</p>
  )}
</article>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section id="holiday-inclusion" className="holiday-scroll-section">
              <div className="holiday-inclusion-wrap">
                <div className="holiday-inc-exc-grid">
  <section className="holiday-inc-card holiday-inc-card--green">
    <h2>Inclusions</h2>
    <ul>
      {inclusions.map((item, index) => (
  <li key={`${String(item)}-${index}`}>
    {typeof item === "object" ? item.name || item.title || "" : item}
  </li>
))}
    </ul>
  </section>

  {facilities.length > 0 && (
  <section className="holiday-scroll-section">
    <div className="holiday-tab-panel">
      <h2>Facilities & Amenities</h2>

      <ul>
        {facilities.map((item, index) => (
          <li key={`facility-${index}`}>
            {typeof item === "object"
              ? item.name || item.title || item.description || ""
              : item}
          </li>
        ))}
      </ul>
    </div>
  </section>
)}

<div className="holiday-availability-card">
  <h3>Booking & Departure Information</h3>

  {pkg.booking_start_date && (
    <p>
      <strong>Booking Starts:</strong>{" "}
      {formatDate(pkg.booking_start_date)}
    </p>
  )}

  {pkg.booking_end_date && (
    <p>
      <strong>Booking Ends:</strong>{" "}
      {formatDate(pkg.booking_end_date)}
    </p>
  )}

  {pkg.departure_type && (
  <p>
    <strong>Departure:</strong>{" "}
    {pkg.departure_type === "recurring"
      ? `Every ${pkg.departure_interval_days} days`
      : pkg.departure_type === "fixed"
        ? "Fixed Dates"
        : "Daily"}
  </p>
)}

  {pkg.departure_type === "recurring" &&
    pkg.departure_start_date && (
      <p>
        <strong>First Departure:</strong>{" "}
        {pkg.departure_start_date}
      </p>
    )}

  {pkg.departure_type === "fixed" &&
    departureDates.length > 0 && (
      <div>
        <strong>Departure Dates:</strong>

        <ul>
          {departureDates.map((date, index) => (
            <li key={`${date}-${index}`}>
              {date}
            </li>
          ))}
        </ul>
      </div>
    )}
</div>

  <section className="holiday-inc-card holiday-inc-card--red">
    <h2>Exclusions</h2>
    <ul>
      {exclusions.map((item, index) => (
  <li key={`${String(item)}-${index}`}>
    {typeof item === "object" ? item.name || item.title || "" : item}
  </li>
))}
    </ul>
  </section>
</div>
            
              </div>
            </section>

    
          </section>

          <aside className="holiday-booking-side">
            <div className="holiday-price-card">
              <div className="holiday-price-top">
  <span>Package Price</span>

  {Number(pkg.base_price) > Number(pkg.selling_price) && (
    <del>{formatPackagePrice(pkg.base_price)}</del>
  )}

  <strong>
    {formatPackagePrice(Number(pkg.selling_price) || 0)}

    <small>
      {pkg.pricing_basis === "per_room"
        ? " Per Room"
        : pkg.pricing_basis === "per_booking"
          ? " Per Booking"
          : " Per Person"}
    </small>
  </strong>

  {Number(pkg.discount_percent) > 0 && (
    <span>
      {Number(pkg.discount_percent)}% OFF
    </span>
  )}
</div>

<div className="holiday-price-breakdown">
  <div>
    <span>Base Price</span>
    <strong>{formatPackagePrice(Number(pkg.base_price) || 0)}</strong>
  </div>

  {Number(pkg.discount_amount) > 0 && (
    <div>
      <span>Discount</span>
      <strong>
        - {formatPackagePrice(Number(pkg.discount_amount))}
      </strong>
    </div>
  )}

  {Number(pkg.gst) > 0 && (
    <div>
      <span>GST</span>
      <strong>{formatPackagePrice(Number(pkg.gst))}</strong>
    </div>
  )}

  {Number(pkg.other_tax) > 0 && (
    <div>
      <span>Other Taxes</span>
      <strong>{formatPackagePrice(Number(pkg.other_tax))}</strong>
    </div>
  )}

  {Number(pkg.service_fee) > 0 && (
    <div>
      <span>Service Fee</span>
      <strong>{formatPackagePrice(Number(pkg.service_fee))}</strong>
    </div>
  )}

  {Number(pkg.other_charges) > 0 && (
    <div>
      <span>Other Charges</span>
      <strong>{formatPackagePrice(Number(pkg.other_charges))}</strong>
    </div>
  )}

  <div>
    <span>Total Price</span>
    <strong>{formatPackagePrice(Number(pkg.final_price) || 0)}</strong>
  </div>
</div>
              <div className="holiday-price-actions">
                <button className="holiday-price-book" type="button" onClick={() => setShowCalendar(true)}>Book Now</button>       
              </div>
            </div>

            <div className="holiday-info-card">
              <p>
                <Clock3 size={17} />
                <span><strong>Duration :</strong> {pkg.duration.replace("/", " / ")}</span>
              </p>
              <p>
                <MapPin size={17} />
                <span><strong>Places to Visit :</strong> {places.join(" | ") || destination}</span>
              </p>
              {inclusions.length > 0 && (
  <>
    <div className="holiday-includes-pill">Package Includes</div>

    <div className="holiday-includes">
      {inclusions.map((item, index) => (
        <span key={`include-${index}`}>
          {typeof item === "object"
            ? item.name || item.title || item.description || ""
            : item}
        </span>
      ))}
    </div>
  </>
)}
            </div>

            <div className="holiday-help-card">
  <PhoneCall size={34} />
  <div>
    <h3>Need Help?</h3>
    <p>Call us : 9355471999</p>
    <p>Mail us : Holidays@makebeetrip.com</p>
    <p>Website : makebeetrip.com</p>
  </div>
</div>
          </aside>
        </div>
      </section>

      <button
  className="holiday-enquire-float"
  type="button"
  onClick={() => navigate("/contact")}
>
  <MessageSquareText size={20} />
  <span>Enquire<br />Now</span>
</button>

      <button className="holiday-back-floating" type="button" onClick={() => navigate(`/holidays/packages/${placeSlug}`)}>
        <ArrowLeft size={16} />
        Back to Packages
        <ArrowRight size={16} />
      </button>

      {showCalendar && (
        <div className="holiday-calendar-overlay" onClick={(event) => event.target === event.currentTarget && setShowCalendar(false)}>
          <section className="holiday-calendar-modal" role="dialog" aria-modal="true" aria-label="Select travel date">
            <button className="holiday-calendar-close" type="button" onClick={() => setShowCalendar(false)}>×</button>
            <p>Choose Travel Date</p>
            <h2>{pkg.title}</h2>
            <span>
  {pkg.duration}
  {pkg.group_size ? ` | ${pkg.group_size} Travellers` : ""}
</span>
            <div className="holiday-date-field">
              <label htmlFor="holiday-departure" className="holiday-field-label">DEPARTURE</label>
              <input
  id="holiday-departure"
  type="date"
  value={selectedTravelDate}
  min={(() => {
    const today = new Date();
    today.setDate(
      today.getDate() + Number(pkg.booking_min_advance_days || 0)
    );

    return today.toISOString().split("T")[0];
  })()}
  max={(() => {
    const today = new Date();
    today.setFullYear(
      today.getFullYear() + Number(pkg.booking_max_advance_years || 2)
    );

    return today.toISOString().split("T")[0];
  })()}
  onChange={(e) => {
    const value = e.target.value;

    if (!isValidDepartureDate(value)) {
      setTravelDateError(
        "Selected date is not an available departure date for this package."
      );
      setSelectedTravelDate("");
      return;
    }

    setTravelDateError("");
    setSelectedTravelDate(value);
  }}
  className="holiday-date-input"
/>
              {travelDateError && (
                <p role="alert" style={{ color: "#e55a4e", fontSize: ".8rem", marginTop: 6 }}>
                  {travelDateError}
                </p>
              )}
            </div>
            <div className="holiday-calendar-actions">
              <button type="button" onClick={() => setShowCalendar(false)}>Cancel</button>
              <button
  type="button"
  disabled={!selectedTravelDate}
  onClick={() => {
    if (!selectedTravelDate) return;

    if (!isValidDepartureDate(selectedTravelDate)) {
      alert("Please select a valid available departure date.");
      return;
    }

    setShowCalendar(false);

    navigate(`/holidays/confirm/${placeSlug}/${packageSlug}`, {
      state: {
        packageId: pkg.id,
        from: location.state?.from || "New Delhi",
        destination:
          pkg.destination ||
          pkg.place ||
          pkg.location ||
          destination,
        travelDate: selectedTravelDate,
        adults: 2,
  children: 0,
  infants: 0,
        pricingBasis: pkg.pricing_basis || "per_person",
      },
    });
  }}
>
  Continue <ArrowRight size={15} />
</button>
            </div>
          </section>
        </div>
      )}

      
    </main>
  );
}
